import { encounterCreatureField } from '../../shared/array-types';
import { isZoneAnchor, statusPaused, zoneKey } from '../zones/zone-membership';
import { createFormulaReadContext } from './formula-read-context';
import { readInventoryField } from './inventory-raw-readers';
import { readItemField } from './item-field-reader';
import { leanStatusEntry } from './status-row-construction';
import { phpJsonInt, phpJsonString, phpJsonTruthy } from './php-json-values';
import { assertUncomputedFromBase, fromBaseRecord, fromBaseUuid, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import { normalizeSavedStatus } from './from-base-encounter-shell';
import type { FromBaseEncounterShellMetadata } from './from-base-encounter-shell';
import { surfaceField } from './from-base-encounter-media';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import { readNodeItemId } from './layer-cell-readers';
import type { ItemFieldRow } from './item-field-reader';

const difficult = '60160881-7F8B-A3ED-4D1A-D80C8397E634';
const marks = (value: unknown): boolean => typeof value === 'string' && value.toUpperCase() === difficult;
interface ZoneSource {
    entity_id: string;
    statuses: { target: readonly Row[]; caster: readonly Row[] };
    inventory: readonly Row[];
    encounter_creatures_arr: readonly Row[];
    floor_order?: readonly string[];
    roster_order?: readonly string[];
    missing_definition_ids?: readonly string[];
}

/** Native zone-key census needs saved anchors, not geometry or member statistics.
 * Only the definition's terrain projection is read. Template/enchantment changes
 * to that projection remain a named producer gap, including on provider donors. */
export function compileEncounterZoneKeysFromBase(base: ZoneSource, catalog: Readonly<Record<string, Row>>,
    metadata: Pick<FromBaseEncounterShellMetadata, 'layerCatalog' | 'abilityTypes'>,
    onRowRefusal?: (id: string, reason: string, kind: 'member' | 'floor') => void): string[] {
    assertUncomputedFromBase(base); assertUncomputedFromBase(catalog, 'catalog');
    const unavailable = (detail: string): never => { throw new FromBaseStageMissing('terrain-zone-definition-finalization', detail); };
    const checkSource = (row: ItemFieldRow): void => {
        for (const name of ['template_id', 'enchantment_template_id', 'enchantment_template_applied'])
            if (phpJsonTruthy(surfaceField(row, name))) unavailable(phpJsonString(readItemField(row, 'item_id')) + ':' + name);
    };
    const definition = (id: string): Row => {
        const row = catalog[id] ?? catalog[id.toUpperCase()];
        if (!row) throw new FromBaseInputMissing(base.missing_definition_ids?.some(miss => miss.toUpperCase() === id.toUpperCase())
            ? 'zone-definition-known-miss' : 'zone-definition', id);
        if (phpJsonInt(readItemField(row, 'item_deleted')) !== 0) throw new TypeError(`Deleted zone definition: ${id}`);
        checkSource(row);
        return row;
    };
    const readers = createFormulaReadContext({ catalogRows: metadata.layerCatalog, abilityTypes: metadata.abilityTypes,
        aggregationStrategy: name => metadata.layerCatalog[name]?.aggregation_strategy ?? 'host_wins',
        resolveCarrierRef: definition, resolveInventoryInstance: id => unavailable('inventory:' + id) });
    const providers = createCatalogDefaultProviderReader({ catalogRows: metadata.layerCatalog, nodeItemId: readNodeItemId,
        resolveCarrierRef: definition });
    const checked = new Set<string>();
    const checkProviders = (row: ItemFieldRow): void => {
        checkSource(row);
        const id = readNodeItemId(row);
        if (id !== null && checked.has(id)) return;
        if (id !== null) checked.add(id);
        for (const donor of providers.providers(row)) checkProviders(donor);
    };
    const ordered = (rows: readonly Row[], order: readonly string[] | undefined, id: (row: Row) => unknown, stage: string): Row[] => {
        if (!rows.length) return [];
        if (!order) throw new FromBaseStageMissing(stage, base.entity_id);
        const byId = new Map(rows.map(row => [phpJsonString(id(row)).toUpperCase(), row]));
        if (byId.size !== rows.length || new Set(order.map(key => key.toUpperCase())).size !== order.length
            || order.some(key => !byId.has(key.toUpperCase())) || order.length !== (stage === 'zone-roster-order' ? Math.min(50, rows.length) : rows.length))
            throw new TypeError(`Invalid ${stage}`);
        return order.map(key => byId.get(key.toUpperCase())!);
    };
    const keys = new Set<string>();
    const scan = (rows: readonly Row[], holder: string, creature: boolean): void => {
        for (const raw of rows) {
            if (!fromBaseRecord(raw) || phpJsonInt(surfaceField(raw, 'status_deleted')) !== 0) throw new TypeError('Invalid saved zone status');
            // The same raw normalizer and lean helper feed the full status overlay.
            const status = leanStatusEntry(normalizeSavedStatus(raw)) as Row;
            if (statusPaused(status) || !isZoneAnchor(status)) continue;
            const target = phpJsonString(surfaceField(raw, 'status_target_id'));
            if (creature && target !== holder) continue; // Native aura holder comparison is case-sensitive.
            if (!creature && target.toUpperCase() !== holder.toUpperCase()) throw new TypeError('Invalid zone status membership');
            const id = surfaceField(raw, 'status_item_id');
            if (!fromBaseUuid(id)) throw new TypeError('Invalid zone definition identity');
            const def = definition(id);
            checkProviders(def);
            if (!marks(readers.layerValue(def, 'terrain_type')) && !marks(readers.layerValue(def, 'terrain_created'))
                && !(readItemField(def, 'item_type') === 'terrain_type' && marks(readItemField(def, 'item_id')))) continue;
            const key = zoneKey(status);
            if (!key) throw new FromBaseStageMissing('terrain-zone-key', id);
            keys.add(key);
        }
    };
    const targetRows = (host: Row): readonly Row[] => {
        if (!fromBaseRecord(host.statuses) || !Array.isArray(host.statuses.target)) throw new TypeError('Invalid saved zone status closure');
        return host.statuses.target as Row[];
    };
    scan(base.statuses.target, base.entity_id, false);
    const scanChild = (host: Row, id: string, kind: 'member' | 'floor'): void => {
        const before = new Set(keys);
        try { scan(targetRows(host), id, kind === 'member'); }
        catch (error) {
            if (!onRowRefusal || !(error instanceof FromBaseInputMissing || error instanceof FromBaseStageMissing)) throw error;
            for (const key of keys) if (!before.has(key)) keys.delete(key);
            onRowRefusal(id, error.message, kind);
        }
    };
    const floor = base.inventory.filter(row => phpJsonString(readInventoryField(row, 'possess_id')).toUpperCase() === base.entity_id.toUpperCase());
    for (const host of ordered(floor, base.floor_order, row => readInventoryField(row, 'inventory_id'), 'zone-floor-order'))
        scanChild(host, phpJsonString(readInventoryField(host, 'inventory_id')), 'floor');
    for (const host of ordered(base.encounter_creatures_arr, base.roster_order, row => row.entity_id, 'zone-roster-order'))
        scanChild(host, phpJsonString(encounterCreatureField(host.encounter_creatures, 'encounter_creature_id')), 'member');
    return [...keys];
}
